import "whatwg-fetch";
import { context } from "./context";
import { http } from "./http";
import { ReloginModal } from "../components/ReloginModal";
import { AppInfo, AuthToken, UserMessageType, UserMessages } from "../models";
import { messageHandler } from "./message-handler";
import Cookies from 'js-cookie';
import HeapAnalytics from 'cov-jitux-heap';
import Config from "./config";
import { windowIdManager } from "./window-id-manager";

const authCodeParam = "code",
  relayStateParam = "relay_state",
  stateParam = "state",
  authModeParam = "auth_mode",
  ssoTokenParam = "sso_token",
  appParam = "app",
  mfaParam = "mfa",
  orgIdParam = "org_id";

const authModeSso = "sso",
  authModeSamlSso = "saml_sso",
  authModeEasSso = "saml";

class Auth {
  private _postLoginRedirectKey: string = "post_login_redirect";
  private _reauthenticatePromise: Promise<AuthToken>;
  private signout = false;
  private _heapAnalytics: HeapAnalytics;

  get isUserSignedOut() {
    const id = context.get("identity");
    return id===null;
  }

  private get _baseHref() {
    const baseTag = document.querySelector("base");
    return baseTag ? baseTag.getAttribute("href") : "/";
  }

  private set _isWaitingForAuthCode(isActive: boolean) {
    if (isActive) window.sessionStorage.setItem("waiting_auth_code", "true");
    else window.sessionStorage.removeItem("waiting_auth_code");
  }

  private get _isWaitingForAuthCode(): boolean {
    return window.sessionStorage.getItem("waiting_auth_code") === "true";
  }

  private set _authMode(authMode: string) {
    if (!authMode) window.sessionStorage.removeItem("auth_mode");
    else window.sessionStorage.setItem("auth_mode", authMode);
  }

  private get _authMode() {
    return window.sessionStorage.getItem("auth_mode");
  }

  set oauthClientId(oauthClientId: string) {
    if (!oauthClientId) window.sessionStorage.removeItem("oauthClientId");
    else window.sessionStorage.setItem("oauthClientId", oauthClientId);
  }

  get oauthClientId() {
    var oauth = window.sessionStorage.getItem("oauthClientId");
    return oauth == null ? "" : oauth;
  }

  set redirectUri(redirectUri: string) {
    if (!redirectUri) window.sessionStorage.removeItem("redirectUri");
    else window.sessionStorage.setItem("redirectUri", redirectUri);
  }

  get redirectUri() {
    return window.sessionStorage.getItem("redirectUri");
  }

  get relayState() {
    var ssoRelayState = window.sessionStorage.getItem("ssoRelayState");
    return ssoRelayState == null ? null : ssoRelayState;
  }

  set relayState(ssoRelayState: string) {
    if (!ssoRelayState) window.sessionStorage.removeItem("ssoRelayState");
    else window.sessionStorage.setItem("ssoRelayState", ssoRelayState);
  }

  get userInfo() {
    const id = context.get("identity");
    return id?.userInfo;
  }

  removeRelayState() {
    window.sessionStorage.removeItem("ssoRelayState");
  }

  get hasRelayState(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.has(relayStateParam);
  }

  get accessToken(): string {
    const id = context.get("identity");
    return id && id.accessToken;
  }

  get userName(): string {
    const id = context.get("identity");
    return id && id?.userInfo["user_name"];
  }

  get audience(): string {
    const id = context.get("identity");
    return id && id?.userInfo?.clientInfo?.audience;
  }

  get disableRelayState(): boolean {
    const id = context.get("identity");
    return !!(id && id?.userInfo?.clientInfo?.disableRelayState);
  }

  get appToken(): string {
    return this.app && window.sessionStorage.getItem("token" + this.app.id);
  }

  set appToken(value: string) {
    if (this.app) {
      if (value) {
        console.info("New apptoken " + value);
        window.sessionStorage.setItem("token" + this.app.id, value);
      } else {
        window.sessionStorage.removeItem("token" + this.app.id);
      }
    }
  }

  get app(): AppInfo {
    return JSON.parse(window.sessionStorage.getItem("tokenApp"));
  }

  set app(app: AppInfo) {
    if (app) {
      window.sessionStorage.setItem("tokenApp", JSON.stringify(app));
    } else {
      window.sessionStorage.removeItem("tokenApp");
    }
  }

  get refreshToken(): string {
    const id = context.get("identity");
    return id && id.refreshToken;
  }

  set clientId(value: string) {
    window.sessionStorage.setItem("clientId", value);
  }

  get clientId(): string {
    let id = window.sessionStorage.getItem("clientId");
    if (!id) {
      id = this.generateClientId();
      this.clientId = id;
    }
    return id;
  }

  get windowId(): string {
    return windowIdManager.getWindowId(); 
  }

  set postLoginRedirect(value: string) {
    if (value != null) window.localStorage.setItem(this._postLoginRedirectKey, value);
    else window.localStorage.removeItem(this._postLoginRedirectKey);
  }

  get postLoginRedirect(): string {
    return window.localStorage.getItem(this._postLoginRedirectKey);
  }

  get hasAuthCode(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.has(authCodeParam);
  }

  get hasSsoToken(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.has(ssoTokenParam);
  }

  get hasSignInError(): boolean {
    const params = new URLSearchParams(window.location.search);
    return this._isWaitingForAuthCode && params.has("error") && params.get("error") === "invalid_request";
  }

  get isSsoAuthMode(): boolean {
    return this._authMode === authModeSso || this._authMode === authModeSamlSso || this._authMode === authModeEasSso;
  }

  constructor() {
    this.addHttpInterceptor();

    context.changedEvent.subscribe((key: string, value: { idToken: string; accessToken: any }, oldValue: { idToken: string; accessToken: any }) => {
      if (key === "identity") {
        // Identity set to null means user signed out.
        if (value && (value?.accessToken !== oldValue?.accessToken)) {
          console.log("Update tokens");
          const oldPayload = oldValue?.accessToken?.split(".")[1];
          const oldUserInfo = oldPayload && JSON.parse(window.atob(oldPayload));

          const newPayload = value?.accessToken?.split(".")[1];
          const newUserInfo = newPayload && JSON.parse(window.atob(newPayload));

          let oldUserName = oldUserInfo ? oldUserInfo["mi-loginid"] ? oldUserInfo["mi-loginid"] : oldUserInfo["user_name"] : null;
          let newUserName = newUserInfo ? newUserInfo["mi-loginid"] ? newUserInfo["mi-loginid"] : newUserInfo["user_name"] : null;

          if (oldUserName?.toLowerCase() != newUserName?.toLowerCase()) {
            window.location.href = window.location.origin;
          }

          this.configureLoginPopup();
          this.fetchAppToken()
            .then(() => {
              ReloginModal.hide();
            });
        }
        if (!value) {
          console.log("Clear tokens");
          this.clear();
          if ((!this.signout || window.opener) && !this.hasAuthCode) {
            window.close();
          } else {
            if (this.hasAuthCode && this.isSsoAuthMode) {
              const params = new URLSearchParams(window.location.search);
              let authCode = params.has(authCodeParam) ? params.get(authCodeParam) : null;

              this.authenticateAuthCode(authCode);
            }
          }
        } else if (value?.idToken) {
          // Exchange the auth code with access and refresh tokens (non-MFA workflow).
          this._authMode = null;
          this.authenticate(value.idToken)
            .then(() => {
              const url = this.postLoginRedirect;
              this.postLoginRedirect = null;
              if (url != null) window.location.href = url;
            })
            .catch(error => {
              this.handleAuthenticationError(error);
            });
        } else if (!oldValue || (oldValue && oldValue.idToken && value.idToken)) {
          // OAuth 2.0 auth code exchanged; remove the query string.
          const targetUri = new URL(window.location.href);
          targetUri.searchParams.delete(authCodeParam);
          targetUri.searchParams.delete(authModeParam);
          window.history.replaceState(null, null, targetUri.href);
          window.location.href = targetUri.href;
        }
      }
    });
  }


  addAuthHeader(headers: HeadersInit, url: String = null): any {
    headers = headers || {};

    if (this.accessToken) {
      let token = this.accessToken;

      let tokenUrl = this.app?.appConfigurations?.appTokenUrl || this.app?.apiUrl;
      if (this.app?.appConfigurations?.appToken === "true" && url.includes(tokenUrl)) {
        if (this.appToken) {
          token = this.appToken;
          console.log("Using app token: " + url);
        } else {
          console.log("App token missing: " + url);
        }
      }

      const authKey = "Authorization";
      const authValue = "Bearer " + token;

      const temp = headers as Headers;
      if (temp && typeof temp.set == "function") {
        (<Headers>headers).set(authKey, authValue);
      } else
        return {
          ...headers,
          Authorization: authValue
        };
    }
    return headers;
  }

  addHttpInterceptor(): void {
  const fetch = window.fetch;

  window.fetch = (url: RequestInfo, args: RequestInit) => {
    args = args || { headers: {} };

    const requestUrl = typeof url === "string"
      ? url
      : url instanceof Request
        ? url.url
        : (<Request>url).url;
    const isHeap = requestUrl.indexOf("heap-api.com") >= 0;
    const requestHeaders = new Headers(args.headers || {});
    const isAuthUrl = requestUrl.indexOf("api/auth/") >= 0;
    const isAuthCheckUrl = requestUrl.indexOf("api/auth/check") >= 0;
    const isJituxUrl = requestUrl.indexOf("api/") === 0;

    if (isJituxUrl) {
      requestHeaders.set("Client-Id", this.oauthClientId);
    }

    const hasExistingAuth = requestHeaders.has("Authorization");

    if (!isHeap && (!isAuthUrl || isAuthCheckUrl) && !hasExistingAuth)
      this.addAuthHeader(requestHeaders, requestUrl);

    if (isAuthUrl) {
      requestHeaders.set("X-Mi-ClientBaseHref", this._baseHref);
    }

    const appUrl = this.app?.appConfigurations?.appTokenUrl || this.app?.apiUrl;
    const additionalHeaders = this.app?.appConfigurations?.additionalHeaders;
    if (appUrl && requestUrl.includes(appUrl) && additionalHeaders) {
      additionalHeaders.split(",").map(headerName => headerName.trim()).filter(Boolean).forEach(headerName => {
        switch (headerName.toLowerCase()) {
          case "x-mi-windowid":
            requestHeaders.set("X-Mi-WindowId", this.windowId);
            break;
          default:
            throw new Error(`Unsupported app additional header: ${headerName}`);
        }
      });
    }

    args.headers = requestHeaders;

    return fetch(url, args).then((response: Response) => {
      if (response.status === 401 && (!isAuthUrl || isAuthCheckUrl) && !this.hasAuthCode) {
        return this.reauthenticate()
          .then(data => {
            return this.fetchAppToken()
              .then(data => {
                this.addAuthHeader(requestHeaders, requestUrl);
                return fetch(url, args);
              });
          });
      }
      return response;
    }).catch(error => {
      console.log(error);
      throw error;
    });
  };
}

  /**
   * Authenticates user based on token
   * @param identityToken
   * @returns
   */
  authenticate(identityToken: string): Promise<AuthToken> {
    const body = JSON.stringify({ Token: identityToken });
    return http
      .fetch("api/auth/token", {
        method: "POST",
        body: body,
        headers: {
          "Content-Type": "application/json"
        }
      })
      .then((result: any) => {
        if (result.access_token) return result;
        else throw result;
      })
      .then((data: AuthToken) => {
        return this.setTokens(data).then(() => data);
      });
  }

  /**
   * Authenticate user with expired access token validating password.
   * @param accessToken Expired access token
   * @param password User's password
   * @returns
   */
  authenticatePassword(accessToken: string, password: string): Promise<void> {
    const body = JSON.stringify({ Username: "relogin", Password: password, AccessToken: accessToken });
    return http
      .fetch("api/auth/relogin", {
        method: "POST",
        body: body,
        headers: {
          "Content-Type": "application/json"
        }
      })
      .then((result: any) => {
        if (result.access_token) return result;
        else throw result;
      })
      .then((data: AuthToken) => {
        return this.setTokens(data);
      });
  }

  /**
   * Redirect to Auth Service (for MFA login, or SSO token).  Auth Service redirects back with
   * authorization code after successful login.
   */
  redirectToAuthService() {
    const params = new URLSearchParams(window.location.search);
    let app = null;
    if (params.has(appParam)) {
      app = params.get(appParam);
      window.sessionStorage.setItem(appParam, app);
    } else {
      app = window.sessionStorage.getItem(appParam);
    }
    const organizationId = window.sessionStorage.getItem("organizationId") || (params.has(orgIdParam) ? params.get(orgIdParam) : null);
    const ssoToken = params.has(ssoTokenParam) ? params.get(ssoTokenParam) : null;

    const currentUri = new URL(window.location.href);
    let redirectUri = this.redirectUri;
    if (!redirectUri) redirectUri = currentUri.origin + "/";

    let authUri = `${this._baseHref}auth/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`;
    if (this.oauthClientId) authUri = `${authUri}&client_id=${this.oauthClientId}`;
    if (app) authUri = `${authUri}&app=${encodeURIComponent(app)}`;
    if (organizationId) authUri = `${authUri}&organization_id=${encodeURIComponent(organizationId)}`;
    if (!!ssoToken) {
      authUri = `${authUri}&sso_token=${encodeURIComponent(ssoToken)}`;
      this._authMode = authModeSso;
    } else this._authMode = null;

    this._isWaitingForAuthCode = true;

    window.location.href = authUri;
  }

  /**
   * Exchanges Auth Code with access tokens using OAuth 2.0 Authorization Code flow and
   * establishes session for the user.
   */
  authenticateAuthCode(authCode: String = null) {
    this.authenticateAuthCodePromise(authCode);
  }

  setOauth() {
    const currentUri = new URL(window.location.href);

    if (currentUri.pathname.startsWith("/oauth/")) {
      this.oauthClientId = currentUri.pathname.split('/').pop();
      this.redirectUri = currentUri.origin + currentUri.pathname;
    }

    if (!this.oauthClientId) {
      this.oauthClientId = "";
      this.redirectUri = currentUri.origin + "/";
    }
  }

  authenticateAuthCodePromise(authCode: String = null): Promise<void> {
    const params = new URLSearchParams(window.location.search);

    if (!authCode) {
      authCode = params.has(authCodeParam) ? params.get(authCodeParam) : null;
    }

    if (!authCode) throw `Auth code required for authorization code flow`;

    this._isWaitingForAuthCode = false;

    const callBackCookieName = "mi-oidc-auth-callback-uri";
    if (!Cookies.get(callBackCookieName)) {
      this.setCookie(callBackCookieName, this.redirectUri);
    }

    //backward support, not used for enlyte360
    const currentUri = new URL(window.location.href);
    if (currentUri.searchParams.has(authModeParam) && currentUri.searchParams.get(authModeParam)?.toLowerCase() === authModeSamlSso) {
      this._authMode = authModeSamlSso;
      currentUri.searchParams.delete(authCodeParam);
      currentUri.searchParams.delete(stateParam);
      auth.redirectUri = encodeURIComponent(currentUri.href);
    }

    try {
      this.relayState = this.hasRelayState ? decodeURIComponent(params.get(relayStateParam)) : null;
    } catch (error) {
      console.log("Unable to set relay state" + window.location.href);
    }

    const body = JSON.stringify({ AuthorizationCode: authCode, RedirectUri: this.redirectUri });
    return http
      .fetch("api/auth/code", {
        method: "POST",
        body,
        headers: {
          "Content-Type": "application/json"
        }
      })
      .then((result: any) => {
        if (result.access_token) return result;
        else throw result;
      })
      .then((data: AuthToken) => {
        return this.setTokens(data);
      })
      .catch(error => {
        this.handleAuthenticationError(error);
      });
  }

  clear(): void {
    window.sessionStorage.removeItem("clientId");
  }

  signOut(): void {
    this.clear();
    this.signout = true;

    let cookieDmain = this.getCookieDomain();

    Cookies.remove("saml-token", { domain: cookieDmain, path: '/saml-api', secure: true, sameSite: 'none' });
    Cookies.remove('samlSource', 'payor', { domain: cookieDmain, path: '/', secure: true, sameSite: 'none' });

    context.set("identity", null);    
  }

  private getCookieDomain(): string {
    let host = window.location.hostname;
    let cookieDmain = host;

    let domainParts = host.split('.');
    if (domainParts.length > 1) {
      domainParts.shift();
      cookieDmain = '.' + domainParts.join('.');
    }

    return cookieDmain;
  }

  generateClientId(): string {
    return (
      "<JIT-UX>" +
      new Date().valueOf().toString(16) +
      "-" +
      Math.floor((1 + Math.random()) * 0x10000)
        .toString(16)
        .substring(1)
    ); // Randomizer.
  }


  redirectToAuthServicePromise(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.redirectToAuthService();
    });
  }

  /**
   * Access token rotation (refresh token flow)
   * @returns
   */
  reauthenticate(): Promise<AuthToken> {
    if (this._reauthenticatePromise) return this._reauthenticatePromise;

    this._reauthenticatePromise = http
      .fetch("api/auth/refresh", {
        method: "POST"
      })
      .then((result: any) => {
        this._reauthenticatePromise = null;
        if (result.access_token) return result;
        else if (result.status !== 200) {
          if (result.status == 401) {
            this.clearStaleIdentity();
          } else {
            context.set("identity", null);
            return this.redirectToAuthServicePromise().catch(() =>
              this.clear()
            );
          }
        } else throw result;
      })
      .then((data: AuthToken) => {
        if (data) {
          return this.setTokens(data).then(() => data);
        }
        return data;
      })
      .catch(error => {
        this._reauthenticatePromise = null;
        this.handleAuthenticationError(error);
        return null;
      });

    return this._reauthenticatePromise;
  }

  configureLoginPopup() {
    if (window['loginPopup']) {
      clearTimeout(window['loginPopup']);
    }
    let refreshToken = auth.refreshToken;
    const payload = refreshToken?.split(".")[1];
    const userInfo = payload && JSON.parse(window.atob(payload));
    var now = Date.now() / 1000;
    let timeout = ((((userInfo.exp - now) - 10)) * 1000);
    console.log("Logout in " + timeout + "ms");
    const interval = setTimeout(() => {
      ReloginModal.show(Promise.resolve());
    }, timeout);
    window['loginPopup'] = interval;
  }

  async openLoginPopup(): Promise<boolean> {
    const params = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,
        width=500,height=600,left=100,top=100`;

    const organizationId = window.sessionStorage.getItem("organizationId");

    const currentUri = new URL(window.location.href);
    let redirectUri = this.redirectUri;
    if (!redirectUri) redirectUri = currentUri.origin + "/";

    let authUri = `${this._baseHref}auth/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`;
    if (this.oauthClientId) authUri = `${authUri}&client_id=${this.oauthClientId}`;
    if (organizationId) authUri = `${authUri}&organization_id=${encodeURIComponent(organizationId)}`;
    const popup = window.open(authUri, 'ReloginPopup', params);
    return true;
  }

  async openSpLoginPopup(): Promise<boolean> {
    const params = `scrollbars=no,resizable=no,status=no,location=no,toolbar=no,menubar=no,
        width=500,height=600,left=100,top=100`;

    const currentUri = new URL(window.location.href);
    let redirectUri = this.redirectUri;
    if (!redirectUri) redirectUri = currentUri.origin + "/";

    let authUri = `https://saml.uat.enlyte360.com/saml-sp/v1/sp?audience=${this.audience}`;    
    const popup = window.open(authUri, 'ReloginPopup', params);
    return true;
  }

  async checkAuthentication(): Promise<boolean> {
    const response: Response = await http.fetch("api/auth/check");
    const authenticated = response.status === 200;
    if (!authenticated) {
      this.clearStaleIdentity();
    }
    return authenticated;
  }

  setTokens(data: AuthToken): Promise<void> {
    if (!data?.access_token) {
      return Promise.resolve();
    }

    localStorage.removeItem("suppress_jitux_ad_signout");
    this.setCookie("saml-token", data.access_token);

    const userInfo = this.parseUserInfo(data.access_token);
    if (!userInfo) {
      this.setIdentityContext(data, null);
      return Promise.resolve();
    }

    this._authMode = userInfo.amr?.[0] === "saml" ? authModeEasSso : null;

    return this.fetchPortalUserData(data.access_token)
      .then((portalUser) => {
        if (!portalUser) return null;
        return this.fetchClientConfig(data.access_token).then((clientInfo) => {
          userInfo.clientInfo = clientInfo;
          userInfo.portalUserInfo = portalUser;
          return this.identifyUserForAnalytics(userInfo, portalUser);
        }).then(() => {
          console.log("Heap Analytics: user identified");
        });
      })
      .catch((error) => {
        console.error("Error during user identification:", error);
      })
      .finally(() => {
        this.setIdentityContext(data, userInfo);
      });
  }

  private parseUserInfo(accessToken: string): any | null {
    try {
      const payload = accessToken.split(".")[1];
      return payload ? JSON.parse(window.atob(payload)) : null;
    } catch (error) {
      console.error("Unable to parse and set user info in context.", error);
      return null;
    }
  }

  identifyUserForAnalytics(userInfo: any, portalUser: any): void {
    try {
      const userProperties = {
        environment: Config.get("env"),
        userId: portalUser?.portalUserId || '',
        userType: portalUser?.userTypeDescr || '',
        organizationId: userInfo?.organizationId || userInfo?.company_code || '',
        clientName: portalUser?.clientNm || '',
        ssoUser: userInfo?.amr?.[0] === "saml" ? "true" : "false",
        applicationName: "E360"
      };
      this._heapAnalytics = new HeapAnalytics();
      if (this._heapAnalytics?.identifyUser) {
        this._heapAnalytics.identifyUser(portalUser?.portalUserId || '', userProperties);
      }
    } catch (e) {
      console.log("Heap Analytics: Failed to identify user with portal data -", e);
      this.identifyUserFallback(userInfo);
    }
  }

  private identifyUserFallback(userInfo: any): void {
    try {
      const fallbackProperties = {
        //email: userInfo?.["mi-email"] || '',
        organizationId: userInfo?.organizationId || userInfo?.company_code || '',
        environment: Config.get("env")
      };
      this._heapAnalytics = new HeapAnalytics();
      if (this._heapAnalytics?.identifyUser) {
        this._heapAnalytics.identifyUser(userInfo?.user_name, fallbackProperties);
      }
    } catch (e) {
      console.log("Heap Analytics: Failed to identify user with fallback data -", e);
    }
  }

  private setIdentityContext(data: AuthToken, userInfo: any): void {
    context.set("identity", {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      userInfo
    });
  }

  fetchAppToken() {
    if (this.app && this.accessToken && this.app?.appConfigurations?.appToken === "true") {
      let provider = this.app?.appConfigurations?.provider ? this.app?.appConfigurations?.provider : this.app.id;
      const ssoEndpoint = this.app?.appConfigurations?.ssoEndpoint;
      if (!ssoEndpoint) {
        return Promise.reject(new Error("App token SSO endpoint is not configured."));
      }
      const tokenUrl = new URL(ssoEndpoint, window.location.href);
      tokenUrl.searchParams.set("provider", provider);
      tokenUrl.searchParams.set("clientDashboardId", String(context.get("clientDashboardId") ?? 0));

      return fetch(tokenUrl.toString(), {
        headers: {
          "X-Mi-WindowId": this.windowId
        }
      })
        .then(response => {
          if (!response.ok) {
            console.error(`Failed to fetch app token. HTTP ${response.status}: ${response.statusText}`);
            throw new Error(`Unable to get token for app.`);
          }
          return response.text();
        })
        .then(value => {
          const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

          if (!jwtPattern.test(value)) {
            console.error(`Invalid JWT format for appToken. Received: ${value.substring(0, 100)}...`);
            throw new Error(`Invalid JWT format for appToken.`);
          }

          this.appToken = value;
          return value;
        })
        .catch(error => {
          console.error("Unable to get app token.", error);
          throw error; // Re-throw so caller knows it failed
        });
    } else {
      return Promise.resolve();
    }
  }

  redirectToAuthStatusPage() {
    this._authMode = null;
    this._isWaitingForAuthCode = false;
    window.location.href = `${this._baseHref}sso/authstatus.html`;
  }

  clearStaleIdentity() {
    if (context.get("identity")) {
      this.postLoginRedirect = window.location.pathname == this._baseHref ? window.location.pathname : window.location.href;
      localStorage.setItem("suppress_jitux_ad_signout", "true");
      context.set("identity", null);
    }
  }

  private handleAuthenticationError(error: any) {
    try {
      error.json().then(data => {
        let msg = data.detail;
        if (data.body) {
          let detail = JSON.parse(data.body);
          if (detail.code == "403003") {
            msg = "Enlyte user is not active in LDAP.";
          } else if (detail.code == "403002") {
            msg = "Your organization has established a Single Sign-On (SSO) connection from your system, to Enlyte360. Please access Enlyte360 from the SSO connection going forward. Contact your system administrator for assistance.";
          }
        }

        messageHandler.handle({
          type: UserMessageType.Custom,
          title: "ALERT!",
          message: msg,
          hideClose: true
        });
      });
    } catch (ex) { }
  }

  windowOpenerAccessible(): boolean {
    if (!window.opener) {
      return false;
    }

    try {
      const openerOrigin = window.opener.location.origin;
      return true;
    } catch (error) {
      return false;
    }
  }

  private setCookie(name: string, value: string): void {
    let upperleveldomain = window.location.hostname;

    Cookies.remove(name, { domain: upperleveldomain, path: '/' });
    Cookies.set(name, value, { domain: upperleveldomain, path: '/', secure: true, sameSite: 'none' });
  }

  private fetchPortalUserData(accessToken: String): Promise<any> {
    const heapUserUrl = Config.get("heapUserUrl");

    if (!heapUserUrl) {
      console.log("Heap Analytics: Portal user API URL not configured");
      return Promise.resolve(null);
    }

    return fetch(heapUserUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      }
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Portal user API returned ${response.status}`);
        }
        return response.json();
      })
      .catch(error => {
        console.log('Heap Analytics: fetchPortalUserData request failed -', error);
        return null;
      });
  }

  private async fetchClientConfig(accessToken: String): Promise<any> {
    try {
      const clientConfigUrl = Config.get("clientConfigUrl");

      if (!clientConfigUrl) {
        console.log("Client config URL not found, skipping Heap Analytics initialization");
        return null;
      }

      const response = await fetch(clientConfigUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        }
      });

      if (!response.ok) {
        throw new Error(`Client config API returned ${response.status}`);
      }

      return await response.json();
    } catch (e) {
      console.log("Heap Analytics: fetchClientConfig failed -", e);
      return null;
    }
  }

}

export const auth = new Auth();